import React from 'react';
import { useTranslation } from 'react-i18next';

interface FormFieldProps {
  id: string;
  name: string;
  label: string;
  type?: 'text' | 'number' | 'textarea' | 'checkbox';
  placeholder?: string;
  defaultValue?: string | number | boolean;
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  min?: number;
  max?: number;
  step?: number | string;
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  formatValue?: (value: string) => string;
  autoFocus?: boolean;
  help?: string; // Texte d'aide à afficher sous le champ
  success?: string; // Message de succès à afficher en vert
}

const FormField: React.FC<FormFieldProps> = ({
  id,
  name,
  label,
  type = 'text',
  placeholder,
  defaultValue,
  required = false,
  disabled = false,
  maxLength,
  min,
  max,
  step,
  className,
  labelClassName,
  inputClassName,
  onChange,
  onBlur,
  formatValue,
  autoFocus = false,
  help,
  success
}) => {
  const { t } = useTranslation();
  // Gérer le formatage spécial pour la durée (minutes -> heures)
  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (name === 'duration' && formatValue) {
      const value = e.target.value;
      e.target.value = formatValue(value);
    }
    
    if (onChange) {
      onChange(e);
    }
  };

  // Afficher le nombre de caractères restants si maxLength est défini
  const [charCount, setCharCount] = React.useState(
    typeof defaultValue === 'string' ? defaultValue.length : 0
  );

  const handleCharCount = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCharCount(e.target.value.length);
    if (onChange) {
      onChange(e);
    }
  };

  const baseInputClasses = "w-full p-2 border border-gray-300 dark:border-gray-700 rounded-md dark:bg-gray-800 text-gray-900 dark:text-white";
  const hasError = className?.includes('error');
  const hasSuccess = !!success && !hasError;
  const inputErrorClasses = hasError ? "border-red-500 dark:border-red-700 focus:ring-red-500" : "";
  const inputSuccessClasses = hasSuccess ? "border-green-500 dark:border-green-700 focus:ring-green-500" : "";

  return (
    <div className={`mb-4 ${className || ''}`}>
      <label htmlFor={id} className={`block text-sm font-medium text-gray-900 dark:text-white mb-1 ${labelClassName || ''}`}>
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      {type === 'textarea' ? (
        <>
          <textarea
            id={id}
            name={name}
            className={`${baseInputClasses} ${inputErrorClasses} ${inputSuccessClasses} min-h-[100px] ${inputClassName || ''}`}
            defaultValue={defaultValue as string}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            maxLength={maxLength}
            onChange={maxLength ? handleCharCount : onChange}
            onBlur={onBlur}
            autoFocus={autoFocus}
          />
          {maxLength && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
              {charCount}/{maxLength} {t('common.characters')}
            </div>
          )}
        </>
      ) : type === 'checkbox' ? (
        <div className="flex flex-col">
          <div className="flex items-center">
            <input
              type="checkbox"
              id={id}
              name={name}
              className={`mr-2 ${inputClassName || ''}`}
              defaultChecked={defaultValue as boolean}
              disabled={disabled}
              onChange={onChange}
              onBlur={onBlur}
            />
            <label htmlFor={id} className="text-sm text-gray-900 dark:text-white">{label}</label>
          </div>
          {help && (
            <div className={`text-xs mt-1 ml-6 ${hasError ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
              {help}
            </div>
          )}
          {success && (
            <div className="text-xs mt-1 ml-6 text-green-500">
              {success}
            </div>
          )}
        </div>
      ) : (
        <>
          <input
            type={type}
            id={id}
            name={name}
            className={`${baseInputClasses} ${inputErrorClasses} ${inputSuccessClasses} ${inputClassName || ''}`}
            defaultValue={defaultValue as string | number}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            maxLength={maxLength}
            min={min}
            max={max}
            step={step}
            onChange={maxLength ? handleCharCount : (name === 'duration' ? handleDurationChange : onChange)}
            onBlur={onBlur}
            autoFocus={autoFocus}
          />
          {maxLength && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
              {charCount}/{maxLength} {t('common.characters')}
            </div>
          )}
          {help && (
            <div className={`text-xs mt-1 ${hasError ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'}`}>
              {help}
            </div>
          )}
          {success && (
            <div className="text-xs mt-1 text-green-500">
              {success}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FormField;
